import { Tooltip } from '@mui/material';

import './PriceCell.css';
import { PriceCellProps, PriceType } from './PriceCell.types';
import React from 'react';

/**
 * Renders a price cell displaying fuel prices with optional discounts, price changes, and differences
 */
export const PriceCell: React.FC<PriceCellProps> = React.memo(({
  brand,
  fuelType,
  hideDiscounts,
  formatPrice,
  hideSales = false,
  priceType = PriceType.MODE
}) => {
  const fuelPrice = brand.fuelPrices?.[fuelType];
  if (!fuelPrice) return <span></span>;

  const mainPrice = priceType === PriceType.MODE ? fuelPrice.mode : fuelPrice.average;
  const discountPrice = priceType === PriceType.MODE ? fuelPrice.modeWithDiscount : fuelPrice.averageWithDiscount;
  const priceChange = priceType === PriceType.MODE ? fuelPrice.modePriceChange : fuelPrice.averagePriceChange;
  const stationCount = fuelPrice.stationCount;
  const mainDiff = priceType === PriceType.MODE ? fuelPrice.modePriceDifference : fuelPrice.averagePriceDifference;
  const discountDiff = priceType === PriceType.MODE ? fuelPrice.modeDiscountDifference : fuelPrice.averageDiscountDifference;
  const priceColor = priceType === PriceType.MODE ? fuelPrice.modePriceColor : fuelPrice.averagePriceColor;
  const priceChangeValue = priceType === PriceType.MODE ? fuelPrice.modePriceChangeValue : fuelPrice.averagePriceChangeValue;

  let cellClass = '';
  if (priceColor) {
    switch (priceColor) {
      case 'green':
        cellClass = 'price-lower';
        break;
      case 'yellow':
        cellClass = 'price-higher';
        break;
      case 'red':
        cellClass = 'price-critical';
        break;
    }
  }

  /**
   * Returns the appropriate icon for price change direction
   * @param change - Price change direction ('up', 'down', or other)
   * @returns Unicode arrow character or empty string
   */
  const getPriceChangeIcon = (change: string) => {
    switch (change) {
      case 'up':
        return '↑';
      case 'down':
        return '↓';
      default:
        return '';
    }
  };

  /**
   * Formats price difference with appropriate sign and decimal places
   * @param diff - The price difference value
   * @returns Formatted difference string with sign, or empty string if invalid/zero
   */
  const formatDifference = (diff: number | null | undefined): string => {
    if (diff === null || diff === undefined || typeof diff !== 'number' || isNaN(diff) || diff === 0) return '';
    return `${diff >= 0 ? '+' : ''}${diff.toFixed(2)}`;
  };

  /**
   * Maps color indicators to CSS class names for price differences
   * @param color - Color indicator ('green', 'red', 'grey')
   * @returns Corresponding CSS class name
   */
  const getDifferenceClass = (color?: string): string => {
    switch (color) {
      case 'green':
        return 'difference-positive';
      case 'red':
        return 'difference-negative';
      case 'grey':
        return 'difference-neutral';
      default:
        return '';
    }
  };

  /**
   * Renders a price value with tooltip and change indicators
   * @param price - The price value to display
   * @param className - Additional CSS class for styling
   * @param tooltip - Tooltip text to show on hover
   * @returns JSX element with formatted price and change indicators
   */
  const renderPriceValue = (price: number | null | undefined, className: string, tooltip: string) => (
    <Tooltip
      title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{tooltip}</div>}
      placement="top"
      enterDelay={200}
      leaveDelay={100}
    >
      <div
        className={`price-value ${className} ${priceChange === 'new' ? 'price-new' : priceChange === 'removed' ? 'price-removed' : ''}`}
      >
        {priceChange === 'removed' ? (
          ''
        ) : (
          <>
            <span>{formatPrice(price)}</span>
              {priceChangeValue !== 0 && Math.abs(Number(priceChangeValue)) > 0.001 && (
              <>
                <span
                    className={Number(priceChangeValue) >= 0 ? 'price-diff-positive' : 'price-diff-negative'}
                >
                    ({Number(priceChangeValue) >= 0 ? '+' : ''}
                    {Number(priceChangeValue).toFixed(2)})
                  <span
                    className={priceChange === 'up' ? 'price-arrow up' : priceChange === 'down' ? 'price-arrow down' : ''}
                  >
                    {getPriceChangeIcon(priceChange)}
                  </span>
                </span>
              </>
            )}
          </>
        )}
      </div>
    </Tooltip>
  );

  return (
    <div className={`price-container ${cellClass}`}>
      {!hideDiscounts ? (
        <div className={`price-grid${hideSales && brand.is_reference ? ' no-sales-row' : ''}`}>
          {renderPriceValue(
            mainPrice,
            'mode-price',
            brand.is_reference ? 'Ціна стели' : `Кількість АЗК:\n${stationCount}`
          )}
          {brand.is_reference ? (
            <Tooltip
              title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{'Ціна з урахуванням\nзнижки'}</div>}
              placement="top"
              enterDelay={200}
              leaveDelay={100}
            >
              <div className={`price-value discount-price price-${priceChange}`}>
                {priceChange === 'removed'
                  ? ''
                  : discountPrice !== null && discountPrice !== undefined && discountPrice !== mainPrice
                    ? formatPrice(discountPrice)
                    : ''}
              </div>
            </Tooltip>
          ) : (
            <Tooltip
              title={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{'Ціна з урахуванням\nзнижки'}</div>}
              placement="top"
              enterDelay={200}
              leaveDelay={100}
            >
              <div className={`price-value discount-price price-${priceChange}`}>
                {priceChange === 'removed'
                  ? ''
                  : discountPrice !== null && discountPrice !== undefined && discountPrice !== mainPrice
                    ? formatPrice(discountPrice)
                    : ''}
              </div>
            </Tooltip>
          )}
          {brand.is_reference ? (
            <>
              {!hideSales && (
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{'Срдн. реал. по НП за\nпот. тиж.'}</div>
                  }
                  placement="top"
                  enterDelay={200}
                  leaveDelay={100}
                >
                  <div className="price-value fuel-realization-cell">
                    {fuelPrice.sales?.currentWeekAvg
                      ? fuelPrice.sales.currentWeekAvg.toLocaleString('uk-UA', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })
                      : ''}
                  </div>
                </Tooltip>
              )}
              {!hideSales && (
                <Tooltip
                  title={
                    <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
                      {'Приріст поточного до\nминулого тижня'}
                    </div>
                  }
                  placement="top"
                  enterDelay={200}
                  leaveDelay={100}
                >
                  <div className="price-value fuel-growth-cell">
                    {fuelPrice.sales?.weekGrowth != null && !!fuelPrice.sales?.currentWeekAvg ? (
                      <span
                        className={
                          fuelPrice.sales.weekGrowth > 0
                            ? 'positive-change'
                            : fuelPrice.sales.weekGrowth < 0
                              ? 'negative-change'
                              : ''
                        }
                      >
                        <span>
                          {fuelPrice.sales.weekGrowth >= 0 ? '+' : ''}
                          {Number(fuelPrice.sales.weekGrowth || 0).toFixed(1)}%
                        </span>
                        <span
                          className={`price-arrow ${fuelPrice.sales.weekGrowth > 0 ? 'up' : fuelPrice.sales.weekGrowth < 0 ? 'down' : ''}`}
                        >
                          {fuelPrice.sales.weekGrowth > 0 ? '↑' : fuelPrice.sales.weekGrowth < 0 ? '↓' : ''}
                        </span>
                      </span>
                    ) : (
                      ''
                    )}
                  </div>
                </Tooltip>
              )}
            </>
          ) : (
            <>
              <div className={`price-value difference-value ${getDifferenceClass(fuelPrice.differenceColor)}`}>
                {formatDifference(mainDiff)}
              </div>
              <div className={`price-value difference-value ${getDifferenceClass(fuelPrice.differenceColor)}`}>
                {discountPrice !== null && discountPrice !== undefined && discountPrice !== mainPrice
                  ? formatDifference(discountDiff)
                  : ''}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className={`price-single${hideSales && brand.is_reference ? ' no-sales-row' : ''}${brand.is_reference && !hideSales ? ' okko-single' : ''}`}>
          {renderPriceValue(mainPrice, '', brand.is_reference ? 'Ціна стели' : `Кількість АЗК:\n${stationCount}`)}
          {brand.is_reference && !hideSales ? (
            <div className="price-single-bottom">
              <Tooltip
                title={
                  <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{'Срдн. реал. по НП за\nпопер. день'}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div className="regional-sales-value">
                  {fuelPrice.sales?.currentWeekAvg
                    ? fuelPrice.sales.currentWeekAvg.toLocaleString('uk-UA', {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })
                    : ''}
                </div>
              </Tooltip>
              <Tooltip
                title={
                  <div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{'Приріст поточного до\nминулого тижня'}</div>
                }
                placement="top"
                enterDelay={200}
                leaveDelay={100}
              >
                <div className={`regional-sales-growth ${fuelPrice.sales?.weekGrowth > 0 ? 'positive-change' : fuelPrice.sales?.weekGrowth < 0 ? 'negative-change' : ''}`}>
                  {fuelPrice.sales?.weekGrowth != null && !!fuelPrice.sales?.currentWeekAvg ? (
                    <span className={fuelPrice.sales?.weekGrowth > 0 ? 'positive-change' : fuelPrice.sales?.weekGrowth < 0 ? 'negative-change' : ''}>
                      <span>{fuelPrice.sales?.weekGrowth >= 0 ? '+' : ''}{Number(fuelPrice.sales?.weekGrowth || 0).toFixed(1)}%</span>
                      <span className={`sales-arrow ${fuelPrice.sales?.weekGrowth > 0 ? 'up' : fuelPrice.sales?.weekGrowth < 0 ? 'down' : ''}`}>
                        {fuelPrice.sales?.weekGrowth > 0 ? '↑' : fuelPrice.sales?.weekGrowth < 0 ? '↓' : ''}
                      </span>
                    </span>
                  ) : ''}
                </div>
              </Tooltip>
            </div>
          ) : !brand.is_reference ? (
            <div className={`price-value difference-value ${getDifferenceClass(fuelPrice.differenceColor)}`}>
              {formatDifference(mainDiff)}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
});
